import { useState, useCallback, useRef, useEffect } from 'react'
import { TIMEOUTS } from '../constants'
import type { Message } from '../types'

interface UseMessageOptions {
  // Default timeout for auto-dismiss (0 = no auto-dismiss)
  defaultTimeout?: number
}

interface UseMessageResult {
  message: Message | null
  showMessage: (type: Message['type'], text: string, timeout?: number) => void
  showError: (text: string, timeout?: number) => void
  showSuccess: (text: string, timeout?: number) => void
  clearMessage: () => void
}

/**
 * Hook for managing notification messages with auto-dismiss.
 */
export function useMessage(options: UseMessageOptions = {}): UseMessageResult {
  const { defaultTimeout = TIMEOUTS.MESSAGE_DISMISS } = options

  const [message, setMessage] = useState<Message | null>(null)
  const timeoutRef = useRef<number | null>(null)

  // Clear any pending timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const clearMessage = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setMessage(null)
  }, [])

  const showMessage = useCallback((
    type: Message['type'],
    text: string,
    timeout?: number
  ) => {
    // Clear any existing timeout
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    setMessage({ type, text })

    // Set auto-dismiss timeout if specified
    const dismissTime = timeout ?? defaultTimeout
    if (dismissTime > 0) {
      timeoutRef.current = window.setTimeout(() => {
        setMessage(null)
        timeoutRef.current = null
      }, dismissTime)
    }
  }, [defaultTimeout])

  const showError = useCallback((text: string, timeout?: number) => {
    showMessage('error', text, timeout ?? TIMEOUTS.MESSAGE_DISMISS_LONG)
  }, [showMessage])

  const showSuccess = useCallback((text: string, timeout?: number) => {
    showMessage('success', text, timeout)
  }, [showMessage])

  return {
    message,
    showMessage,
    showError,
    showSuccess,
    clearMessage,
  }
}
